import { compact } from 'lodash-es';
import { InvoiceDisplayStatus, InvoicePaymentDisplayStatusType, InvoiceSortingColumn } from 'anchor-scout-common/types';
import { AccountingStatusFilter } from '~/business-logic/qboStatusFilterLogic';
import { invoiceModel } from '~/models';
import { InvoiceOptimisticUpdateType } from '~/providers/InvoiceChangesObservableProvider';
import { getFormattedTimeRange } from './DateFilterPicker';
export var InvoiceHeaderItemID = /*#__PURE__*/ function(InvoiceHeaderItemID) {
    InvoiceHeaderItemID["invoiceId"] = "invoiceId";
    InvoiceHeaderItemID["companyName"] = "companyName";
    InvoiceHeaderItemID["status"] = "status";
    InvoiceHeaderItemID["issueDate"] = "issueDate";
    InvoiceHeaderItemID["dueDate"] = "dueDate";
    InvoiceHeaderItemID["total"] = "total";
    InvoiceHeaderItemID["additionalInformation"] = "additionalInformation";
    return InvoiceHeaderItemID;
}({});
export const invoiceHeaderItemIdToSortingColumn = {
    ["invoiceId"]: InvoiceSortingColumn.Number,
    ["companyName"]: InvoiceSortingColumn.CompanyName,
    ["status"]: InvoiceSortingColumn.Status,
    ["additionalInformation"]: InvoiceSortingColumn.PaymentStatus,
    ["issueDate"]: InvoiceSortingColumn.IssuedOn,
    ["dueDate"]: InvoiceSortingColumn.DueDate,
    ["total"]: InvoiceSortingColumn.TotalAmount
};
const invoiceDisplayStatusToLabel = {
    [InvoiceDisplayStatus.Issued]: 'Issued',
    [InvoiceDisplayStatus.Overdue]: 'Overdue',
    [InvoiceDisplayStatus.PaymentInitiated]: 'Processing',
    [InvoiceDisplayStatus.Paid]: 'Paid',
    [InvoiceDisplayStatus.Voided]: 'Voided',
    [InvoiceDisplayStatus.Refunded]: 'Refunded',
    [InvoiceDisplayStatus.RefundFailed]: 'Refund failed',
    [InvoiceDisplayStatus.PaymentDisputed]: 'Disputed',
    [InvoiceDisplayStatus.PartiallyRefunded]: 'Partially refunded'
};
export function getPaymentStatusFilterOptions(isVendor, paymentStatusFilterCounters) {
    return [
        InvoicePaymentDisplayStatusType.StoppedPayment,
        InvoicePaymentDisplayStatusType.RequiresPaymentApproval,
        InvoicePaymentDisplayStatusType.RequiresPaymentValidation,
        InvoicePaymentDisplayStatusType.PaymentFailed,
        InvoicePaymentDisplayStatusType.PaymentMethodNotSet,
        InvoicePaymentDisplayStatusType.PaymentMethodUnverified,
        InvoicePaymentDisplayStatusType.Processing,
        InvoicePaymentDisplayStatusType.Collected,
        InvoicePaymentDisplayStatusType.Paid,
        InvoicePaymentDisplayStatusType.PaidExternally,
        InvoicePaymentDisplayStatusType.Refunded,
        InvoicePaymentDisplayStatusType.Disputed,
        InvoicePaymentDisplayStatusType.RefundRequested,
        InvoicePaymentDisplayStatusType.None
    ].map((status)=>({
            value: status,
            label: getPaymentStatusFilterText(status, isVendor),
            counter: paymentStatusFilterCounters[status] || 0
        })).filter((opt)=>opt.counter);
}
function getPaymentStatusFilterText(status, isVendor) {
    switch(status){
        case InvoicePaymentDisplayStatusType.None:
            return 'None';
        case InvoicePaymentDisplayStatusType.Collected:
            return 'Collected';
        case InvoicePaymentDisplayStatusType.Paid:
            return 'Deposited';
        case InvoicePaymentDisplayStatusType.PaidExternally:
            return 'Paid externally';
        case InvoicePaymentDisplayStatusType.PaymentFailed:
            return 'Payment failed';
        case InvoicePaymentDisplayStatusType.PaymentMethodNotSet:
            return 'Payment method not set';
        case InvoicePaymentDisplayStatusType.PaymentMethodUnverified:
            return 'Payment method not verified';
        case InvoicePaymentDisplayStatusType.Pending:
            return 'Issued';
        case InvoicePaymentDisplayStatusType.Processing:
            return 'Payment initiated';
        case InvoicePaymentDisplayStatusType.Refunded:
            return 'Refund initiated';
        case InvoicePaymentDisplayStatusType.RefundFailed:
            return 'Refund failed';
        case InvoicePaymentDisplayStatusType.RequiresPaymentApproval:
            return isVendor ? 'Client approval required' : 'Pending your approval';
        case InvoicePaymentDisplayStatusType.RequiresPaymentValidation:
            return 'Payment validation required';
        case InvoicePaymentDisplayStatusType.StoppedPayment:
            return 'Client stopped payment';
        case InvoicePaymentDisplayStatusType.Disputed:
            return 'Disputed';
        case InvoicePaymentDisplayStatusType.RefundRequested:
            return 'Refund requested';
    }
}
export function getInvoiceStatusFilterOptions(statusFilterCounters) {
    return [
        InvoiceDisplayStatus.Issued,
        InvoiceDisplayStatus.PaymentInitiated,
        InvoiceDisplayStatus.Paid,
        InvoiceDisplayStatus.Overdue,
        InvoiceDisplayStatus.Voided,
        InvoiceDisplayStatus.Refunded,
        InvoiceDisplayStatus.RefundFailed,
        InvoiceDisplayStatus.PaymentDisputed,
        InvoiceDisplayStatus.PartiallyRefunded
    ].map((statusId)=>({
            value: statusId,
            label: invoiceDisplayStatusToLabel[statusId],
            counter: statusFilterCounters[statusId] || 0
        })).filter((opt)=>opt.counter);
}
export function getAccountingStatusFilterOptions(accountingStatusFilterCounters) {
    const syncedCounter = accountingStatusFilterCounters.sync || 0;
    const unsyncedCounter = accountingStatusFilterCounters.unsync || 0;
    return [
        {
            value: AccountingStatusFilter.All,
            label: 'All',
            counter: syncedCounter + unsyncedCounter
        },
        {
            value: AccountingStatusFilter.Synced,
            label: 'Synced',
            counter: syncedCounter
        },
        {
            value: AccountingStatusFilter.NotSynced,
            label: 'Not synced',
            counter: unsyncedCounter
        }
    ].filter((opt)=>opt.counter);
}
export function applyOptimisticUpdate(invoices, optimisticUpdate, now) {
    const { type, invoiceId } = optimisticUpdate;
    const updateInvoice = (update)=>invoices.map((invoice)=>invoice.id === invoiceId ? update(invoice) : invoice);
    switch(type){
        case InvoiceOptimisticUpdateType.Refunded:
            return updateInvoice((inv)=>invoiceModel.setRefunded(inv, optimisticUpdate.timestamp, now));
        case InvoiceOptimisticUpdateType.RefundRequested:
            {
                const { userId, reason } = optimisticUpdate;
                return updateInvoice((inv)=>invoiceModel.setRefundRequested(inv, userId, reason));
            }
        case InvoiceOptimisticUpdateType.Voided:
            return updateInvoice((inv)=>invoiceModel.setVoided(inv, optimisticUpdate.userId));
        case InvoiceOptimisticUpdateType.PaymentApproved:
            return updateInvoice((inv)=>invoiceModel.setPending(inv, now));
        case InvoiceOptimisticUpdateType.PaymentConfirmed:
            {
                const { isVendor } = optimisticUpdate;
                return updateInvoice((inv)=>invoiceModel.setPaymentConfirmed(inv, isVendor));
            }
        case InvoiceOptimisticUpdateType.QboIntegrationChanged:
            {
                const { integration } = optimisticUpdate;
                return updateInvoice((inv)=>invoiceModel.setIntegration(inv, integration));
            }
        case InvoiceOptimisticUpdateType.XeroIntegrationChanged:
            {
                const { xeroIntegration } = optimisticUpdate;
                return updateInvoice((inv)=>invoiceModel.setXeroIntegration(inv, xeroIntegration));
            }
        case InvoiceOptimisticUpdateType.PartialRefundRequested:
            {
                const { userId, reason, refundAmount, refundFees } = optimisticUpdate;
                return updateInvoice((inv)=>invoiceModel.setPartialRefundRequested({
                        invoice: inv,
                        userId,
                        reason,
                        refundAmount,
                        refundFees
                    }));
            }
        case InvoiceOptimisticUpdateType.PartiallyRefunded:
            {
                const { timestamp, fees, paymentProcessorType, reason, userId, refundAmount, refundFees, rawTotalAmount } = optimisticUpdate;
                return updateInvoice((inv)=>invoiceModel.setPartiallyRefunded(inv, {
                        timestamp,
                        fees,
                        paymentProcessorType,
                        reason,
                        userId,
                        refundAmount,
                        refundFees,
                        rawTotalAmount
                    }));
            }
        case InvoiceOptimisticUpdateType.Changed:
            {
                const { invoice } = optimisticUpdate;
                return updateInvoice((_inv)=>invoice);
            }
    }
}
export function mapSortAndFiltersRequestParams(param) {
    let { date, now, sort, sortIds, search, status, paymentStatus, qbo, xero } = param;
    const { fromDate, toDate } = getFormattedTimeRange(date, now);
    return {
        search,
        status,
        paymentStatus,
        integration: qbo,
        xeroIntegration: xero,
        issuedOnSince: fromDate ? new Date(fromDate) : undefined,
        issuedOnUntil: toDate ? new Date(toDate) : undefined,
        sort: sort ? {
            field: invoiceHeaderItemIdToSortingColumn[sortIds[sort.colIndex]],
            type: sort.sort
        } : undefined
    };
}
export function getTableDefs(param) {
    let { showQboIntegration, hideDueDate, showXeroIntegration } = param;
    const tableDefs = compact([
        {
            headerItem: {
                id: "invoiceId",
                value: 'Invoice ID'
            },
            columnDef: {
                width: showQboIntegration || showXeroIntegration ? 140 : 100,
                sortable: true,
                mobile: {}
            },
            defaultSortDirection: 'desc'
        },
        {
            headerItem: {
                id: "companyName",
                value: 'Company name'
            },
            columnDef: {
                width: 200,
                sortable: true,
                mobile: {}
            },
            defaultSortDirection: 'asc'
        },
        {
            headerItem: {
                id: "status",
                value: 'Invoice status'
            },
            columnDef: {
                width: [
                    96,
                    140
                ],
                sortable: true,
                mobile: {
                    label: 'Invoice status'
                }
            },
            defaultSortDirection: 'asc'
        },
        {
            headerItem: {
                id: "additionalInformation",
                value: 'Payment status'
            },
            columnDef: {
                width: [
                    220,
                    230
                ],
                sortable: true
            },
            defaultSortDirection: 'asc'
        },
        {
            headerItem: {
                id: "issueDate",
                value: 'Issue Date'
            },
            columnDef: {
                width: [
                    90,
                    100
                ],
                sortable: true,
                mobile: {}
            },
            defaultSortDirection: 'desc'
        },
        !hideDueDate && {
            headerItem: {
                id: "dueDate",
                value: 'Due date'
            },
            columnDef: {
                width: [
                    90,
                    100
                ],
                sortable: true
            },
            defaultSortDirection: 'desc'
        },
        {
            headerItem: {
                id: "total",
                value: 'Total'
            },
            columnDef: {
                width: 84,
                sortable: true,
                hAlign: 'end',
                mobile: {}
            },
            defaultSortDirection: 'desc'
        }
    ]);
    const headerItems = tableDefs.map((d)=>d.headerItem);
    const columnDefs = tableDefs.map((d)=>d.columnDef);
    const sortIds = headerItems.map((item)=>item.id);
    const sortOptions = tableDefs.filter((d)=>d.columnDef.sortable === true).map((d)=>({
            id: d.headerItem.id,
            label: String(d.headerItem.value),
            defaultDirection: d.defaultSortDirection
        }));
    return {
        columnDefs,
        headerItems,
        sortIds,
        sortOptions
    };
}
